import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// styles and images
import "../../project/Project.css";
import Close from "../../../assets/close.svg";

// components and global
import {
  accountDataStore,
  companyStore,
  dbDataStore,
  messageAndErrorStore,
  userStore,
} from "../../../store/Store";

//components
import FormButton from "../../../formComponents/FormButton";
import InputField from "../../../formComponents/InputField";
import DropDown from "../../../formComponents/DropDown";

// hooks
import { useVerifyUnique } from "../../../hooks/useVerifyUnique";
import { useEscape } from "../../../hooks/useEscape";
import InlineInput from "../../../formComponents/InlineInput";
import {
  getFormattedDate,
  parseDateInCentral,
} from "../../../funcs/helperFuncs";

export default function AddAccountCustomerInvoice({
  setTriggerInvoice,
  isAsset,
}) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const currentCompany = userStore((data) => data.currentCompany);
  const companyData = dbDataStore((data) => data.companyData);
  const accountCustomerInvoices = accountDataStore(
    (data) => data.accountCustomerInvoices,
  );
  const setAccountCustomerInvoices = accountDataStore(
    (data) => data.setAccountCustomerInvoices,
  );
  const setAccountCustomerInvoiceTotals = accountDataStore(
    (data) => data.setAccountCustomerInvoiceTotals,
  );
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setInvoiceCounts = companyStore((data) => data.setInvoiceCounts);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  const [pending, setPending] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerInvoice, setCustomerInvoice] = useState({
    id: 0,
    customer_id: "",
    invoice_name: "",
    company_id: currentCompany.id,
    is_asset: isAsset,
    start_date: null,
    end_date: null,
    loads_start: null,
    loads_end: null,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
  });

  const navigate = useNavigate();
  const { exists } = useVerifyUnique(
    accountCustomerInvoices,
    "invoice_name",
    customerInvoice.invoice_name,
    "",
  );
  const handleClose = () => {
    setTriggerInvoice(false);
  };

  useEscape(setTriggerInvoice);
  // get customer options with 'All Customers' added
  useEffect(() => {
    var arr = [{ value: 0, label: "All Customers" }];
    if (companyData) {
      companyData.forEach((c) => {
        arr.push({ value: c.id, label: c.company_name });
      });
    }
    setCustomerOptions(arr);
  }, [companyData]);

  const handleChange = (e) => {
    setCustomerInvoice({
      ...customerInvoice,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelect = (option) => {
    setCustomerInvoice({
      ...customerInvoice,
      customer_id: option.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage(
        "This invoice already exists, please update the name/version",
      );
      setPending(false);
      return;
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const reqBody = customerInvoice;

    reqBody.start_date = getFormattedDate(reqBody.start_date);
    reqBody.end_date = getFormattedDate(reqBody.end_date);
    reqBody.loads_start = parseDateInCentral(reqBody.start_date, false);
    reqBody.loads_end = parseDateInCentral(reqBody.end_date, true);

    if (reqBody.start_date >= reqBody.end_date) {
      toggleNewMessage("The end date must be greater than the start date.");
      setPending(false);
      return;
    }

    const requestOptions = {
      method: "POST",
      headers,
      body: JSON.stringify(reqBody),
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/account-customer-invoice/${customerInvoice.id}`,
      requestOptions,
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setPending(false);
            navigate("/login");
            setAuthStatus(false);
            return Promise.resolve();
          } else {
            setPending(false);
            return Promise.resolve();
          }
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data) {
          setAccountCustomerInvoices(data.customer_invoices);
          setAccountCustomerInvoiceTotals(data.customer_invoice_totals);
          setInvoiceCounts(data.invoice_counts);
          toggleNewMessage(null, "Invoice Added");
          setPending(false);
          setTriggerInvoice(false);
        } else {
          toggleNewMessage("No associated loads with date range");
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  return (
    <div className="popup">
      <div className="popup-inner">
        {!pending && (
          <div className="close">
            <img onClick={handleClose} src={Close} alt="close" />
          </div>
        )}
        <h3>
          {!isAsset
            ? "Generate Customer Freight Invoice"
            : "Generate Customer Asset Invoice"}
        </h3>
        <form onSubmit={handleSubmit}>
          <InputField
            type={"text"}
            name={"invoice_name"}
            title={"Invoice Name"}
            placeholder={"Name of Invoice"}
            onChange={handleChange}
            value={customerInvoice.invoice_name}
            required={true}
          />
          <DropDown
            name={"customer_id"}
            title={"Customer"}
            options={customerOptions}
            onChange={handleSelect}
            className={"dropdown"}
            maxMenuHeight={200}
            required={true}
            value={customerOptions.filter(
              (x) => x.value === customerInvoice.customer_id,
            )}
            placeholder="Select Customer..."
            classOne="mb-3 row field"
            classTwo={"col-sm-2 col-form-label bold-label"}
          />
          <div className="row g-1">
            <InlineInput
              divClass={"col"}
              type={"date"}
              name={"start_date"}
              title={"Start"}
              onChange={handleChange}
              required={true}
              classname={"col-form-label bold-label col-sm-2"}
            />
            <InlineInput
              divClass={"col"}
              type={"date"}
              name={"end_date"}
              title={"End"}
              onChange={handleChange}
              required={true}
              classname={"col-form-label bold-label col-sm-2"}
            />
          </div>
          <FormButton name={"Submit"} pending={pending} />
        </form>
      </div>
    </div>
  );
}
